<template>
    <div v-if="isDialogMode" class="modal-card" @keydown.enter.prevent>
        <header class="modal-card-head">
            <p class="modal-card-title">{{ currentValue ? 'Edit vote' : 'Vote for brew' }}</p>
        </header>
        <section class="modal-card-body">
            <div class="vote-interface dialog-mode">
                <!-- Brew Info Section -->
                <div v-if="brew" class="brew-info-section">
                    <p class="brew-number-title">
                        <span class="brew-number-badge">{{ brew.displayNumber }}</span>
                        <strong class="brew-title-ellipsis">{{ brew.title }}</strong>
                    </p>
                    <p class="brew-brewer">{{ brew.brewer }}</p>
                    <p v-if="brew.style" class="brew-style">{{ brew.style }}</p>
                </div>
                <!-- Edit State for dialog -->
                <div class="vote-edit dialog-edit">
                    <div class="vote-edit-header">
                        <div class="vote-info">
                            <span class="icon">
                                <b-icon icon="star" size="is-small" class="has-text-warning star-outline"></b-icon>
                            </span>
                            <span class="vote-value">{{ (tempValue === 0 && !currentValue) ? 'Not rated' : `${Number(tempValue).toFixed(1)}/10` }}</span>
                        </div>
                    </div>
                    <div class="vote-slider-container">
                        <div class="slider-wrapper">
                            <div class="slider-track-container">
                                <input type="range"
                                       v-model.number="tempValue"
                                       :min="currentValue ? 0.5 : 0"
                                       :max="10"
                                       :step="0.5"
                                       class="vote-slider"
                                       :style="{ '--value-percentage': `${((tempValue - (currentValue ? 0.5 : 0)) / (10 - (currentValue ? 0.5 : 0))) * 100}%` }"
                                       @input="updateTempValue">
                            </div>
                            <div class="slider-labels">
                                <span v-if="!currentValue" class="slider-label" :style="{ left: 'calc(5% - 2px)' }">0.5</span>
                                <span v-if="currentValue" class="slider-label" :style="{ left: '0' }">0.5</span>
                                <span class="slider-label" :style="{ right: '0' }">10</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end;">
            <button type="button" class="button" @click="cancelEdit">Cancel</button>
            <button type="button" class="button is-primary" @click="saveVote" :disabled="tempValue === 0">Save</button>
        </footer>
    </div>
    <div v-else :class="['vote-interface']">
        <!-- Regular State with No Vote (only shown in inline mode) -->
        <div v-if="!isEditing && currentValue === null" class="vote-actions-container justify-end">
            <button class="button is-small is-primary vote-button" @click="handleEdit">
                <span class="icon">
                    <b-icon icon="star" size="is-small"></b-icon>
                </span>
                <span>Vote</span>
            </button>
        </div>

        <!-- Vote Display with Actions (only shown in inline mode) -->
        <div v-if="!isEditing && currentValue !== null" class="vote-display">
            <div class="vote-info">
                <span class="icon">
                    <b-icon icon="star" size="is-small" class="has-text-warning star-outline"></b-icon>
                </span>
                <span class="vote-value">{{ currentValue !== 0 ? `${Number(currentValue).toFixed(1)}/10` : 'Not rated' }}</span>
            </div>
            <div class="vote-actions">
                <button class="button is-small" @click="handleDelete">
                    <span class="icon">
                        <b-icon icon="delete" size="is-small"></b-icon>
                    </span>
                    <span>Remove vote</span>
                </button>
                <button class="button is-small" @click="handleEdit">
                    <span class="icon">
                        <b-icon icon="pencil" size="is-small"></b-icon>
                    </span>
                    <span>Edit</span>
                </button>
            </div>
        </div>

        <!-- Edit State (only for inline mode) -->
        <div v-if="isEditing" class="vote-edit">
            <div class="vote-edit-header">
                <div class="vote-info">
                    <span class="icon">
                        <b-icon icon="star" size="is-small" class="has-text-warning star-outline"></b-icon>
                    </span>
                    <span class="vote-value">{{ tempValue !== 0 ? `${Number(tempValue).toFixed(1)}/10` : 'Not rated' }}</span>
                </div>
                <div class="vote-buttons" v-if="!hideButtons">
                    <button class="button is-small" @click="cancelEdit">
                        <span class="icon">
                            <b-icon icon="close" size="is-small"></b-icon>
                        </span>
                        <span>Cancel</span>
                    </button>
                    <button class="button is-small is-success is-light" @click="saveVote" :disabled="tempValue === 0">
                        <span class="icon">
                            <b-icon icon="check" size="is-small"></b-icon>
                        </span>
                        <span>Save</span>
                    </button>
                </div>
            </div>
            <div class="vote-slider-container">
                <div class="slider-wrapper">
                    <div class="slider-track-container">
                        <input type="range"
                               v-model.number="tempValue"
                               :min="currentValue ? 0.5 : 0"
                               :max="10"
                               :step="0.5"
                               class="vote-slider"
                               :style="{ '--value-percentage': `${((tempValue - (currentValue ? 0.5 : 0)) / (10 - (currentValue ? 0.5 : 0))) * 100}%` }"
                               @input="updateTempValue">
                    </div>
                    <div class="slider-labels">
                        <span v-if="!currentValue" class="slider-label" :style="{ left: 'calc(5% - 2px)' }">0.5</span>
                        <span v-if="currentValue" class="slider-label" :style="{ left: '0' }">0.5</span>
                        <span class="slider-label" :style="{ right: '0' }">10</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { Dialog } from "buefy";
    export default {
        name: 'BrewVoteInterface',

        props: {
            value: {
                type: Number,
                default: null
            },
            isDialogMode: {
                type: Boolean,
                default: false
            },
            brew: {
                type: Object,
                default: null
            },
            hideButtons: {
                type: Boolean,
                default: false
            },
            startInEditMode: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                isEditing: this.startInEditMode || this.isDialogMode,
                tempValue: 0,
                currentValue: null
            }
        },

        watch: {
            value: {
                immediate: true,
                handler(newValue) {
                    this.currentValue = newValue;
                    if (newValue !== null && newValue !== undefined) {
                        // If editing, ensure minimum value is 0.5
                        this.tempValue = Math.max(newValue, 0.5);
                    } else if (this.isDialogMode) {
                        this.tempValue = 0;
                    }
                }
            },
            isDialogMode: {
                immediate: true,
                handler(newValue) {
                    if (newValue) {
                        this.isEditing = true;
                        // If editing an existing vote, ensure minimum value is 0.5
                        if (this.currentValue !== null && this.currentValue !== undefined) {
                            this.tempValue = Math.max(this.currentValue, 0.5);
                        } else {
                            this.tempValue = 0;
                        }
                    }
                }
            }
        },

        methods: {
            startEdit() {
                this.isEditing = true;
                // If editing an existing vote, ensure minimum value is 0.5
                if (this.currentValue !== null && this.currentValue !== undefined) {
                    this.tempValue = Math.max(this.currentValue, 0.5);
                } else {
                    this.tempValue = 0;
                }
            },
            handleEdit() {
                // Emit edit event for parent to handle opening dialog
                this.$emit('edit');
            },

            updateTempValue(event) {
                this.tempValue = Number(event.target.value);
            },
            saveVoteUpdateUI(valueToUpdate) {
                this.currentValue = valueToUpdate;
                this.isEditing = false;
            },
            saveVote() {
                const valueToSave = Number(this.tempValue);
                if (this.isDialogMode) {
                    // In dialog mode, emit the value
                    this.$emit('save', valueToSave);
                } else {
                    // In inline mode, pass the callback
                    this.$emit('save', valueToSave, this.saveVoteUpdateUI);
                }
            },
            getTempValue() {
                return this.tempValue;
            },
            cancelEdit() {
                if (this.isDialogMode) {
                    this.$emit('cancel');
                } else {
                    this.tempValue = this.currentValue || 0;
                    this.isEditing = false;
                    this.$emit('cancel');
                }
            },
            deleteUpdateUI() {
                this.currentValue = null;
                this.isEditing = false;
            },
            handleDelete() {
                this.$dialog.confirm({
                    title: 'Remove vote',
                    message: 'Are you sure you want to remove your vote?',
                    confirmText: 'Remove vote',
                    cancelText: 'Cancel',
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: () => {
                        this.$emit('delete', this.deleteUpdateUI);
                    }
                })
            }
        }
    }
</script>

<style scoped>
    .star-outline {
        -webkit-text-stroke: 1px #696969;
        transform: translateY(-1px);
    }

    .vote-button {
        background-color: #4A90E2;
        color: white;
    }

    .vote-button:hover {
        background-color: #357ABD;
        color: white;
    }

    .vote-interface {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px dotted rgba(136, 145, 144, 0.6);
        margin-bottom: 10px;
        width: 100%;
        max-width: 100%;
    }

    .vote-display {
        background: #f5f5f5;
        padding: 16px;
        border-radius: 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
        width: 100%;
        box-sizing: border-box;
    }

    .vote-info {
        display: flex;
        align-items: center;
        gap: 2px;
    }

    .vote-actions {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .vote-edit {
        background: #f5f5f5;
        padding: 16px;
        border-radius: 8px;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
        width: 100%;
        box-sizing: border-box;
    }

    .vote-value {
        font-weight: 500;
        font-size: 0.9rem;
    }

    .vote-buttons {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        flex: 1;
    }

    .vote-actions-container {
        display: flex;
        justify-content: flex-end;
        width: 100%;
    }

    .vote-edit-header {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        gap: 12px;
    }

    .vote-slider-container {
        padding: 0 8px;
    }

    .slider-wrapper {
        position: relative;
    }

    .slider-track-container {
        position: relative;
        padding: 8px 0;
    }

    .slider-labels {
        position: relative;
        height: 20px;
        margin-top: 0px;
    }

    .slider-label {
        position: absolute;
        font-size: 0.8rem;
        color: #666;
    }

    .slider-label:last-child {
        transform: translateX(0);
    }

    .vote-slider {
        -webkit-appearance: none;
        width: 100%;
        height: 4px;
        background: linear-gradient(to right, #ffdd57 0%, #ffdd57 var(--value-percentage), #dbdbdb var(--value-percentage), #dbdbdb 100%);
        outline: none;
        margin: 0;
        border-radius: 2px;
        position: relative;
    }

    .vote-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #494949;
        cursor: pointer;
        transition: transform 0.2s;
    }

    .vote-slider::-moz-range-thumb {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #494949;
        cursor: pointer;
        border: none;
        transition: transform 0.2s;
    }

    .vote-slider::-webkit-slider-thumb:hover,
    .vote-slider::-moz-range-thumb:hover {
        transform: scale(1.1);
    }

    /* Dialog mode specific styles */
    .dialog-mode {
        margin: 0;
        padding: 0;
        border-top: none;
    }

    .dialog-mode .vote-edit {
        box-shadow: none;
        background: transparent;
        padding: 0;
    }

    .dialog-mode .dialog-edit {
        background: #f5f5f5;
        padding: 16px;
        border-radius: 8px;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .brew-info-section {
        margin-bottom: 24px;
        padding-bottom: 16px;
        border-bottom: 1px solid #e0e0e0;
    }

    .brew-number-title {
        display: flex;
        align-items: center;
        font-size: 1.1rem;
        margin-bottom: 8px;
        min-width: 0; /* Allow flex items to shrink */
    }

    .brew-number-badge {
        background-color: #494949;
        color: white;
        padding: 2px 6px;
        margin-right: 8px;
        display: inline-block;
        min-width: 28px;
        text-align: center;
        border-radius: 9px;
        font-size: 14px;
        font-weight: normal;
        flex-shrink: 0; /* Prevent badge from shrinking */
    }

    .brew-title-ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        display: block;
    }

    .brew-brewer {
        color: #363636;
        font-size: 1rem;
        font-weight: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-bottom: 2px;
    }

    .brew-style {
        color: #363636;
        font-size: 1rem;
        font-style: italic;
        font-weight: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        display: block;
    }

    /* Modal card specific styles */
    .modal-card {
        width: 90vw;
        max-width: 420px;
        min-width: auto;
    }
    
    /* Desktop mode - match brew card container width */
    @media screen and (min-width: 769px) {
        .modal-card {
            width: 420px !important;
            max-width: 420px !important;
        }
    }

    .modal-card-body {
        padding: 1.5rem;
    }

    .modal-card-title {
        font-weight: 600;
    }

    /* Responsive adjustments for small screens */
    @media screen and (max-width: 768px) {
        .modal-card {
            width: 100vw !important;
            max-width: 100vw !important;
            margin: 0;
            border-radius: 0;
        }

        .modal-card-body {
            padding: 1rem;
        }

        .dialog-mode .brew-info-section {
            margin-bottom: 16px;
            padding-bottom: 12px;
        }

        .dialog-mode .brew-number-title {
            font-size: 1rem;
        }

        .dialog-mode .brew-number-badge {
            font-size: 12px;
            padding: 2px 6px;
            min-width: 28px;
        }
        
        .dialog-mode .brew-title-ellipsis {
            max-width: calc(100% - 50px); /* Account for badge width */
        }

        .dialog-mode .vote-edit {
            padding: 12px;
        }

        .modal-card-foot {
            padding: 0.75rem;
        }
    }

    /* Extra small screens (iPhone SE and similar) */
    @media screen and (max-width: 375px) {
        .modal-card {
            width: 100vw;
            height: auto;
            max-height: 100vh;
            margin: 0;
            display: flex;
            flex-direction: column;
        }

        .modal-card-body {
            padding: 0.75rem;
            overflow-y: auto;
            flex: 1;
        }

        .dialog-mode .brew-title-ellipsis {
            max-width: calc(100% - 45px); /* Even smaller for very small screens */
        }
    }
</style>